import { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { UserLayout } from '../layout';
import { useAuth } from '../../context/AuthContext';
import { useDomain } from '../../context/DomainContext';
import { getModuleNavMap } from '../../services/modulesService';

// Core component loaded immediately (home page)
import Home from './Home';

// Dashboard also lazy loaded (not always the initial page)
const Dashboard = lazy(() => import('./Dashboard'));

// Lazy loaded modules - only loaded when accessed
const Modules = lazy(() => import('./Modules'));
const ModuleDetailPage = lazy(() => import('./ModuleDetailPage'));

// Contacts module
const SearchContacts = lazy(() => import('../contacts/SearchContacts'));
const MyContacts = lazy(() => import('../contacts/MyContacts'));
const ContactsMap = lazy(() => import('../contacts/ContactsMap'));

// Calendar module
const Calendar = lazy(() => import('../calendar/Calendar'));
const Events = lazy(() => import('../calendar/Events'));

// Other modules - lazy loaded
const EndroitsPage = lazy(() => import('../places/EndroitsPage'));
const Messages = lazy(() => import('../messages/Messages'));
const UserProfile = lazy(() => import('../profile/UserProfile'));
const Preferences = lazy(() => import('../profile/Preferences'));
import ModuleUsageStrip from '../profile/ModuleUsageStrip';
const Support = lazy(() => import('../support/Support'));
const WorkRequests = lazy(() => import('../work/WorkRequests'));
const WorkOffers = lazy(() => import('../offers/WorkOffers'));
const AdvertisementsPage = lazy(() => import('../advertisements/AdvertisementsPage'));
const WebsitesPage = lazy(() => import('../websites/WebsitesPage'));
const ProductsPage = lazy(() => import('../products/ProductsPage'));
const KnowledgePage = lazy(() => import('../knowledge/KnowledgePage'));
const ExpensesPage = lazy(() => import('../expenses/ExpensesPage'));
const BooksPage = lazy(() => import('../books/BooksPage'));
const MomentsPage = lazy(() => import('../moments/MomentsPage'));
const CarpoolingPage = lazy(() => import('../carpooling/CarpoolingPage'));
const OccupationGroupsPage = lazy(() => import('../occupation-groups/OccupationGroupsPage'));
const ProjectsPage = lazy(() => import('../projects/ProjectsPage'));
const TimeTracking = lazy(() => import('../timetracking/TimeTracking'));
const MeetingsPage = lazy(() => import('../meetings/MeetingsPage'));
const DocumentsPage = lazy(() => import('../documents/DocumentsPage'));
const NotificationsPage = lazy(() => import('../notifications/NotificationsPage'));
const NeighborhoodPage = lazy(() => import('../neighborhood/NeighborhoodPage'));
const ActivitiesPage = lazy(() => import('../activities/ActivitiesPage'));

const DASHBOARD_PAGE_KEY = 'cabaza_dashboard_page';

const VALID_PAGES = ['home', 'search', 'modules', 'contacts', 'messages', 'map', 'profile', 'calendar', 'preferences', 'events', 'places', 'support', 'work', 'offers', 'advertisements', 'websites', 'products', 'knowledge', 'expenses', 'books', 'moments', 'carpooling', 'occupationGroups', 'projects', 'timeTracking', 'meetings', 'documents', 'notifications', 'neighborhood', 'dashboard', 'activities'];

/**
 * Les pages transverses, accessibles depuis une application autonome.
 *
 * Le compte est unique : on doit pouvoir gérer son profil, ses préférences,
 * son abonnement — lieu de la vente croisée — et répondre à un message reçu
 * sur la plateforme, même depuis une adresse d'app.
 */
const PAGES_TRANSVERSES = ['dashboard', 'home', 'profile', 'preferences', 'messages', 'notifications', 'support', 'activities'];

/**
 * La page du module d'une adresse d'app (« EXPENSES » → « expenses »), ou null sur la
 * plateforme.
 */
export const pageDuModule = (moduleCode, navMap) => {
    if (!moduleCode) return null;
    const code = String(moduleCode).toUpperCase();
    const page = (navMap && navMap[code]) || code.toLowerCase();
    return VALID_PAGES.includes(page) ? page : null;
};

// Pages d'atterrissage de la plateforme : sur une adresse d'app, on n'y arrive jamais
// (le visiteur est « coincé » dans son module).
const PAGES_PLATEFORME = ['home', 'dashboard', 'modules', 'search'];

/**
 * Restreint les pages atteignables au périmètre du domaine.
 *
 * Les modules sont en chargement différé, mais seraient montés sur simple
 * changement de page : sans cette barrière, le filtre du menu se contourne
 * par une URL ou une entrée de localStorage laissée par une visite sur la
 * plateforme.
 *
 * @param {string|null} moduleCode - Le module du domaine, ou null pour la plateforme
 * @param {object} navMap - Correspondance code de module → identifiant de page
 */
export const pagesAutoriseesPourDomaine = (moduleCode, navMap) => {
    if (!moduleCode) return VALID_PAGES;

    const code = String(moduleCode).toUpperCase();
    const pageModule = (navMap && navMap[code]) || code.toLowerCase();

    return [...PAGES_TRANSVERSES, pageModule].filter(p => VALID_PAGES.includes(p));
};

/**
 * Loading fallback component
 */
const PageLoader = () => (
    <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cabaza-purple" />
    </div>
);

/**
 * User Dashboard - Main component for logged-in users
 * Uses React.lazy for code splitting - modules are loaded on demand
 */
const UserDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();

    // Sur un sous-domaine d'app, seules les pages de son module et les
    // transverses sont atteignables. Sur la plateforme, la liste complète.
    const { moduleCode } = useDomain();
    const pagesAutorisees = useMemo(
        () => pagesAutoriseesPourDomaine(moduleCode, getModuleNavMap()),
        [moduleCode]
    );

    // Sur une adresse d'app, la page d'atterrissage est le module lui-même, jamais
    // l'accueil de la plateforme.
    const pageApp = pageDuModule(moduleCode, getModuleNavMap());
    const estAtterrissagePlateforme = (page) => !!pageApp && PAGES_PLATEFORME.includes(page);

    // Get initial page from localStorage (persisted across reloads)
    const getInitialPage = () => {
        // Check if page was passed via navigation state (from OccupationsPage, etc.)
        if (location.state?.page) {
            if (pagesAutorisees.includes(location.state.page) && !estAtterrissagePlateforme(location.state.page)) {
                return location.state.page;
            }
        }
        // Une page retenue lors d'une visite sur la plateforme ne doit pas
        // rouvrir un module fermé ici, ni l'accueil de la plateforme sous une app.
        const savedPage = localStorage.getItem(DASHBOARD_PAGE_KEY);
        if (savedPage && pagesAutorisees.includes(savedPage) && !estAtterrissagePlateforme(savedPage)) {
            return savedPage;
        }
        return pageApp || 'dashboard';
    };

    const [currentPage, setCurrentPage] = useState(getInitialPage);
    const [entityId, setEntityId] = useState(null);
    const [tabName, setTabName] = useState(null);

    // Handle navigation state changes (when coming from other pages)
    useEffect(() => {
        if (location.state?.page) {
            if (pagesAutorisees.includes(location.state.page)) {
                setCurrentPage(location.state.page);
                // Set entityId if provided (for deep linking from notifications)
                if (location.state.entityId) {
                    setEntityId(location.state.entityId);
                }
                // Set tabName if provided (for navigating to specific tab)
                if (location.state.tabName) {
                    setTabName(location.state.tabName);
                }
                // Clear the state to prevent re-navigation on refresh
                navigate('/', { replace: true, state: {} });
            }
        }
    }, [location.state, navigate]);

    // Clear entityId when page changes (except when coming from notification)
    useEffect(() => {
        // Only clear if not coming from a notification navigation
        if (!location.state?.entityId) {
            setEntityId(null);
        }
    }, [currentPage]);

    // Sur une adresse d'app, une navigation vers l'accueil (bouton retour, menu…)
    // ramène au module : l'utilisateur ne quitte pas l'app.
    useEffect(() => {
        if (pageApp && PAGES_PLATEFORME.includes(currentPage)) {
            setCurrentPage(pageApp);
        }
    }, [currentPage, pageApp]);

    // Persist current page to localStorage whenever it changes
    useEffect(() => {
        if (pageApp && PAGES_PLATEFORME.includes(currentPage)) return;
        localStorage.setItem(DASHBOARD_PAGE_KEY, currentPage);
    }, [currentPage, pageApp]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const renderPage = () => {
        switch (currentPage) {
            case 'home':
                return <Home onNavigate={setCurrentPage} />;
            case 'search':
                return <SearchContacts />;
            case 'modules':
                return <Modules onNavigate={setCurrentPage} />;
            case 'contacts':
                return <MyContacts onNavigate={setCurrentPage} />;
            case 'messages':
                return <Messages />;
            case 'map':
                return <ContactsMap />;
            case 'profile':
                return <UserProfile />;
            case 'calendar':
                return <Calendar language="fr" onNavigate={setCurrentPage} />;
            case 'events':
                return <Events onNavigate={setCurrentPage} />;
            case 'places':
                return <EndroitsPage onNavigate={setCurrentPage} />;
            case 'support':
                return <Support onNavigate={setCurrentPage} />;
            case 'work':
                return <WorkRequests onNavigate={setCurrentPage} />;
            case 'offers':
                return <WorkOffers onNavigate={setCurrentPage} />;
            case 'advertisements':
                return <AdvertisementsPage onNavigate={setCurrentPage} />;
            case 'websites':
                return <WebsitesPage onNavigate={setCurrentPage} />;
            case 'products':
                return <ProductsPage onNavigate={setCurrentPage} />;
            case 'knowledge':
                return <KnowledgePage onNavigate={setCurrentPage} />;
            case 'expenses':
                return <ExpensesPage onNavigate={setCurrentPage} />;
            case 'books':
                return (
                    <BooksPage
                        initialTab={tabName}
                        initialEntityId={entityId}
                        onEntityViewed={() => { setEntityId(null); setTabName(null); }}
                        onNavigate={setCurrentPage}
                    />
                );
            case 'moments':
                return <MomentsPage onNavigate={setCurrentPage} />;
            case 'carpooling':
                return <CarpoolingPage onNavigate={setCurrentPage} />;
            case 'occupationGroups':
                return <OccupationGroupsPage />;
            case 'projects':
                return <ProjectsPage onNavigate={setCurrentPage} />;
            case 'timeTracking':
                return <TimeTracking />;
            case 'meetings':
                return <MeetingsPage onNavigate={setCurrentPage} initialMeetingId={entityId} onMeetingViewed={() => setEntityId(null)} />;
            case 'documents':
                return <DocumentsPage />;
            case 'notifications':
                return <NotificationsPage />;
            case 'neighborhood':
                return <NeighborhoodPage onNavigate={setCurrentPage} />;
            case 'activities':
                return <ActivitiesPage />;
            case 'dashboard':
                return <Dashboard onNavigate={setCurrentPage} />;
            case 'preferences':
                return (
                    <Preferences
                        onNavigate={(page, moduleCode) => {
                            if (page === 'moduleDetail' && moduleCode) {
                                setEntityId(moduleCode);
                            }
                            setCurrentPage(page);
                        }}
                    />
                );
            case 'moduleDetail':
                return (
                    <ModuleDetailPage
                        moduleCode={entityId}
                        onBack={() => setCurrentPage('preferences')}
                        onNavigate={(page, params) => {
                            if (params?.tab) {
                                localStorage.setItem('cabazaPreferencesMainTab', params.tab);
                            }
                            if (params?.subTab) {
                                localStorage.setItem('cabazaPreferencesModulesSubTab', params.subTab);
                            }
                            setCurrentPage(page);
                        }}
                    />
                );
            default:
                return <Home onNavigate={setCurrentPage} />;
        }
    };

    return (
        <UserLayout
            currentPage={currentPage}
            onNavigate={setCurrentPage}
            onLogout={handleLogout}
        >
            <Suspense fallback={<PageLoader />}>
                <ModuleUsageStrip
                    currentPage={currentPage}
                    onSeeAll={() => {
                        localStorage.setItem('cabazaPreferencesMainTab', 'limits');
                        setCurrentPage('preferences');
                    }}
                />
                {renderPage()}
            </Suspense>
        </UserLayout>
    );
};

export default UserDashboard;
